import React from 'react'
import { Formik, Form, type FormikErrors } from 'formik'
import styled from '@emotion/styled'
import { type InjectedDashIntlProps } from 'intl/intl.types'
import { FormattedMessage, injectIntl } from 'react-intl'
import FormConfirmationBar from 'legacy/components/Forms/FormConfirmationBar'
import { type DropdownOption } from 'legacy/common/types'
import { withTrackEventOnMount } from 'utils/events/hocs'
import {
  ProductImage,
  PRIMARY_IMAGE_FIELD_NAME,
} from 'legacy/routes/catalog/products/product/form-components/ProductImage'
import { ProductInfo } from 'legacy/routes/catalog/products/product/form-components/ProductInfo'
import { ProductDescription } from 'legacy/routes/catalog/products/product/form-components/ProductDescription'
import { ProductCodes } from 'legacy/routes/catalog/products/product/form-components/ProductCodes'
import { ProductAttributes } from 'legacy/routes/catalog/products/product/form-components/ProductAttributes'
import ProductNutrition from 'legacy/routes/catalog/products/product/form-components/ProductNutrition'
import { type ProductFormValues } from 'legacy/routes/catalog/types/product.types'
import SecondaryImages from 'legacy/routes/catalog/products/product/form-components/SecondaryImages'
import { selectInvalidImageAngles } from 'legacy/routes/catalog/products/product/productUtils'

const OuterContainer = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  overflow-y: scroll;
`

const InnerContainer = styled.div`
  width: 100%;
  max-width: 960px;
  padding: 20px;
`

interface Props extends InjectedDashIntlProps {
  product: ProductFormValues
  brands: DropdownOption[]
  onSubmit: (values: ProductFormValues) => void
  isSubmitting: boolean
}

class ProductDetailsForm extends React.Component<Props> {
  validate = (values: ProductFormValues) => {
    const { intl, product } = this.props
    const errors: FormikErrors<ProductFormValues> = {}
    const imageErrors = selectInvalidImageAngles(product, values)

    if (!values.name) {
      errors.name = intl.formatMessage({ id: 'catalog.product.editProduct.errors.name' })
    }

    if (!values.size) {
      errors.size = intl.formatMessage({ id: 'catalog.product.editProduct.errors.size' })
    }

    if (!values.unit_count) {
      errors.unit_count = intl.formatMessage({ id: 'catalog.product.editProduct.errors.unitCount' })
    }

    if (imageErrors.length > 0) {
      imageErrors.forEach(errorAngle => {
        if (errorAngle === PRIMARY_IMAGE_FIELD_NAME) {
          errors[PRIMARY_IMAGE_FIELD_NAME] = intl.formatMessage({
            id: 'catalog.product.editProduct.errors.images',
          })
        } else if (errors.secondary_images) {
          errors.secondary_images[errorAngle] = intl.formatMessage({
            id: 'catalog.product.editProduct.errors.images',
          })
        } else {
          errors.secondary_images = {}

          errors.secondary_images[errorAngle] = intl.formatMessage({
            id: 'catalog.product.editProduct.errors.images',
          })
        }
      })
    }

    return errors
  }

  render() {
    const { product, brands, onSubmit, isSubmitting } = this.props

    return (
      <OuterContainer>
        <InnerContainer>
          <Formik
            initialValues={product}
            onSubmit={onSubmit}
            enableReinitialize
            validate={this.validate}
          >
            {formProps => (
              <Form>
                <React.Fragment>
                  <ProductImage {...formProps} required={false} />
                  <SecondaryImages
                    {...formProps}
                    required={false}
                    disabled={!formProps.values.has_main_image && !formProps.values.primaryImageUrl}
                  />
                  <ProductInfo {...formProps} brands={brands} />
                  <ProductDescription {...formProps} />
                  <ProductCodes {...formProps} disabled />
                  <ProductAttributes {...formProps} disabled />
                  <ProductNutrition {...formProps} />
                  {formProps.dirty && (
                    <FormConfirmationBar
                      error={Object.keys(formProps.errors).length > 0}
                      errorMessage={<FormattedMessage id="catalog.product.form.errorMessage" />}
                      isSubmitting={isSubmitting}
                    />
                  )}
                </React.Fragment>
              </Form>
            )}
          </Formik>
        </InnerContainer>
      </OuterContainer>
    )
  }
}

export default withTrackEventOnMount({
  id: 'catalog.products.product_details.viewed',
  description: 'Viewed the product details page for a specific product',
})(injectIntl(ProductDetailsForm))
